import { SmartImage } from './SmartImage'
interface PageHeroProps {
  /** Path to the full-bleed background image (no transparency needed). */
  image: string
  title: string
  subtitle?: string
  /** `full` ≈ Curators height; `banner` is shorter for image-rich pages. */
  variant?: 'full' | 'banner'
}

/**
 * Full-bleed page hero: cover image + dark gradient overlay with the title and
 * subtitle sitting in the shadowed upper area (the covers are dark at the top,
 * bright toward the center/bottom). Mirrors the Curators hero treatment.
 */
export default function PageHero({ image, title, subtitle, variant = 'full' }: PageHeroProps) {
  const pad =
    variant === 'full'
      ? 'pt-24 sm:pt-32 pb-40 sm:pb-56'
      : 'pt-20 sm:pt-24 pb-24 sm:pb-32'

  return (
    <section className="relative overflow-hidden bg-archive-black">
      {/* Full-bleed cover */}
      <SmartImage
        src={image}
        alt=""
        aria-hidden="true"
        sizes="100vw"
        width={1920}
        height={1080}
        priority
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      {/* Overlay: strongest at top (where text sits), reveals the image mid-frame,
          fades back to black at the bottom to blend into the page. */}
      <div className="absolute inset-0 bg-gradient-to-b from-archive-black/90 via-archive-black/55 to-archive-black" />

      {/* Title + subtitle, in the shadowed upper area */}
      <div className={`relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center ${pad}`}>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-archive-cream drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]">
          {title}
        </h1>
        {subtitle && (
          <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg leading-relaxed text-archive-cream/85 drop-shadow-[0_1px_8px_rgba(0,0,0,0.85)]">
            {subtitle}
          </p>
        )}
      </div>
    </section>
  )
}
